import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCart, 
  getCartItems, 
  addCartItem as addFirestoreCartItem, 
  updateCartItem as updateFirestoreCartItem, 
  removeCartItem as removeFirestoreCartItem, 
  clearCart as clearFirestoreCart,
  subscribeToCart,
  validateCart
} from '@/lib/firestore/cart';
import type { Cart, CartItem as FirestoreCartItem, CartValidationResult } from '@/types/cart';
import { useAuth } from '@/lib/auth';
import { Product } from '@/types/products';
import { Tale } from '@/types/tales';
import { useErrorStore } from '@/lib/error-store';
import { toast } from '@/components/ui/use-toast';
import { isOnline } from '@/lib/firestore/utils';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Define the CartContext type
interface CartContextType {
  cart: Cart | null;
  cartItems: FirestoreCartItem[];
  isLoading: boolean;
  isValidating: boolean;
  isCartValid: boolean;
  validationResult: CartValidationResult | null;
  error: Error | null;
  addItem: (item: Omit<FirestoreCartItem, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateItem: (itemId: string, updates: Partial<Omit<FirestoreCartItem, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  validateCartItems: () => Promise<CartValidationResult>;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { setError } = useErrorStore();
  const [cart, setCart] = useState<Cart | null>(null);
  const [cartItems, setCartItems] = useState<FirestoreCartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [isCartValid, setIsCartValid] = useState(true);
  const [validationResult, setValidationResult] = useState<CartValidationResult | null>(null);
  const [error, setCartError] = useState<Error | null>(null);

  // Handle errors
  const handleError = (error: Error, action: string) => {
    console.error(`Error in cart ${action}:`, error);
    setCartError(error);
    
    if (!isOnline()) {
      setError({
        id: 'cart-offline-error',
        type: 'warning',
        title: 'Network Issue',
        message: 'You appear to be offline. Some cart features may be limited.',
      });
    } else {
      toast({
        variant: "destructive",
        title: "Cart Error",
        description: `Failed to ${action}: ${error.message}`,
      });
    }
  };

  // Initialize cart and set up subscriptions
  useEffect(() => {
    if (!user) {
      setCart(null);
      setCartItems([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Debug: directly fetch the cart to check if it exists
    const debugFetchCart = async () => {
      try {
        const directCart = await getCart(user.uid);
      } catch (error) {
        // Ignore errors here as this is just for debug purposes
      }
    };
    
    // Check if cart exists in both capitalizations
    const checkCartExistence = async () => {
      try {
        // Check 'Carts' collection (capitalized)
        const capitalizedCartsRef = collection(getFirestore(), 'Carts');
        const capitalizedQuery = query(capitalizedCartsRef, where('userId', '==', user.uid));
        const capitalizedSnapshot = await getDocs(capitalizedQuery);
        
        if (!capitalizedSnapshot.empty) {
          const capitalizedCartDoc = capitalizedSnapshot.docs[0];
          // Found a cart in 'Carts'
        } else {
          // No cart in 'Carts' collection
        }
        
        // Check 'carts' collection (lowercase)
        const lowercaseCartsRef = collection(getFirestore(), 'carts');
        const lowercaseQuery = query(lowercaseCartsRef, where('userId', '==', user.uid));
        const lowercaseSnapshot = await getDocs(lowercaseQuery);
        
        if (!lowercaseSnapshot.empty) {
          const lowercaseCartDoc = lowercaseSnapshot.docs[0];
          // Found a cart in 'carts'
        } else {
          // No cart in 'carts' collection
        }
      } catch (error) {
        console.error('Error checking cart existence:', error);
      }
    };
    
    // Set up subscription to Firestore cart
    const unsubscribe = subscribeToCart(user.uid, (updatedCart) => {
      setIsLoading(false);
      
      if (updatedCart) {
        setCart(updatedCart);
        setCartItems(updatedCart.items || []);
      } else {
        setCart(null);
        setCartItems([]);
      }
    });
    
    // Clean up subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [user]);

  // Validate cart whenever items change
  useEffect(() => {
    if (!user || cartItems.length === 0) {
      setIsCartValid(true);
      setValidationResult(null);
      return;
    }

    const performValidation = async () => {
      setIsValidating(true);
      try {
        const result = await validateCart(user.uid);
        setIsCartValid(result.isValid);
        setValidationResult(result);
      } catch (error) {
        handleError(error as Error, 'validate cart');
      } finally {
        setIsValidating(false);
      }
    };

    performValidation();
  }, [user, cartItems]);

  // Add item to cart
  const addItem = async (item: Omit<FirestoreCartItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) {
      // User is not logged in - show a toast with sign in message
      toast({
        variant: "destructive",
        title: "Sign in to continue",
        description: "Sign in to add items to your cart",
        action: (
          <div className="flex gap-2 mt-2">
            <a href="/login" className="px-3 py-1 text-xs bg-primary text-white rounded-md hover:bg-primary/90">
              Sign In
            </a>
            <a href="/signup" className="px-3 py-1 text-xs bg-secondary text-white rounded-md hover:bg-secondary/90">
              Sign Up
            </a>
          </div>
        ),
        duration: 3000
      });
      return;
    }
    
    try {
      // Create a temporary ID for optimistic update
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      const tempItem: FirestoreCartItem = {
        ...item,
        id: tempId,
        createdAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 } as any,
        updatedAt: { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 } as any
      };
      
      // Update local state
      setCartItems(prev => [...prev, tempItem]);
      
      // Then update Firestore
      await addFirestoreCartItem(user.uid, item);
      
      toast({
        description: `${item.name || 'Item'} added`,
        duration: 2000
      });
    } catch (error) {
      // Revert optimistic update
      setCartItems(cartItems);
      handleError(error as Error, 'add item');
    }
  };

  // Update cart item
  const updateItem = async (itemId: string, updates: Partial<Omit<FirestoreCartItem, 'id' | 'createdAt' | 'updatedAt'>>) => {
    if (!user) return;

    try {
      await updateFirestoreCartItem(user.uid, itemId, updates);
    } catch (error) {
      handleError(error as Error, 'update item');
    }
  };

  // Remove item from cart
  const removeItem = async (itemId: string) => {
    if (!user) return;

    try {
      await removeFirestoreCartItem(user.uid, itemId);
      toast({
        description: "Item removed from cart",
        duration: 2000
      });
    } catch (error) {
      handleError(error as Error, 'remove item');
    }
  };

  // Clear cart
  const clearCart = async () => {
    if (!user) return;

    try {
      await clearFirestoreCart(user.uid);
      toast({
        description: "Cart cleared",
        duration: 2000
      });
    } catch (error) {
      handleError(error as Error, 'clear cart');
    }
  };

  // Validate cart items
  const validateCartItems = async (): Promise<CartValidationResult> => {
    if (!user) {
      return { isValid: false, message: "User not authenticated" };
    }

    setIsValidating(true);
    try {
      const result = await validateCart(user.uid);
      setIsCartValid(result.isValid);
      setValidationResult(result);
      return result;
    } catch (error) {
      handleError(error as Error, 'validate cart');
      const result = { isValid: false, message: (error as Error).message };
      setIsCartValid(false);
      setValidationResult(result);
      return result;
    } finally {
      setIsValidating(false);
    }
  };

  const value = {
    cart,
    cartItems,
    isLoading,
    isValidating,
    isCartValid,
    validationResult,
    error,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    validateCartItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}; 
